import React, { useEffect, useState } from 'react';
import {
  KendoUploadOnAddEvent,
  IKendoUploadFile,
  KendoReactFileUpload
} from '@Shared.Angular/kendo.react/KendoReactFileUpload';
import { EmptyGuid, Guid } from '@Shared.Angular/@types/guid';
import { getService } from '@Client/runner.hooks/useService';
import { FormThemeLogoAlignment } from '@Shared.Angular/@types/core/contracts/queryModel/formTheme/formThemeDetail';
import { TextButton } from '@Shared.Angular/flowingly.components/buttons/TextButton';
import IconButton from '@Shared.Angular/flowingly.components/buttons/IconButton';

export type PendingLogoOperation =
  | { type: 'none' }
  | { type: 'add'; file: File; preview: string }
  | { type: 'remove'; removedFileId: Guid }
  | { type: 'replace'; file: File; preview: string; oldFileId: Guid };

const DEFAULT_ALLOWED_TYPES = [
  '.png',
  '.jpg',
  '.jpeg',
  '.jfif',
  '.bmp',
  '.gif'
];
const MAX_IMAGE_DIMENSION = 260;
const alignmentOptions = [
  { label: 'left', value: FormThemeLogoAlignment.Left },
  { label: 'center', value: FormThemeLogoAlignment.Center },
  { label: 'right', value: FormThemeLogoAlignment.Right }
];
type FormThemeLogoSectionProps = {
  logoFileId: Guid | null;
  alignment: FormThemeLogoAlignment;
  pendingLogoOperation: PendingLogoOperation;
  onLogoAdd: (file: File) => void;
  onLogoRemove: () => void;
  onAlignmentChange: (alignment: FormThemeLogoAlignment) => void;
};

export default function FormThemeLogoSection({
  logoFileId,
  alignment,
  pendingLogoOperation,
  onLogoAdd,
  onLogoRemove,
  onAlignmentChange
}: FormThemeLogoSectionProps) {
  const [files, setFiles] = useState<IKendoUploadFile[]>([]);
  const [preview, setPreview] = useState<string | null>(null);
  const [isBusy, setIsBusy] = useState(false);

  const fileService = getService('fileService');
  const notificationService = getService('notificationService');

  useEffect(() => {
    return () => {
      if (preview) {
        URL.revokeObjectURL(preview);
      }
    };
  }, [preview]);

  useEffect(() => {
    let isActive = true;

    const loadPersistedLogo = async () => {
      if (!logoFileId) {
        setFiles([]);
        setPreview(null);
        setIsBusy(false);
        return;
      }

      setIsBusy(true);
      let objectUrl: string;
      try {
        const logoResponse = await fileService.getFileById(logoFileId);
        if (!isActive) {
          URL.revokeObjectURL(objectUrl);
          return;
        }

        objectUrl = URL.createObjectURL(logoResponse.data);
        setFiles([
          {
            id: logoFileId,
            filename: logoResponse.name,
            sizeInKiloBytes: null
          }
        ]);
        setPreview(objectUrl);
      } finally {
        if (isActive) {
          setIsBusy(false);
        }
      }
    };

    if (
      pendingLogoOperation.type === 'add' ||
      pendingLogoOperation.type === 'replace'
    ) {
      const file = pendingLogoOperation.file;
      setFiles([
        {
          filename: file.name,
          id: EmptyGuid,
          sizeInKiloBytes: Math.round(file.size / 1000)
        }
      ]);
      setPreview(pendingLogoOperation.preview);
      setIsBusy(false);
    } else if (pendingLogoOperation.type === 'remove') {
      setFiles([]);
      setPreview(null);
      setIsBusy(false);
    } else {
      loadPersistedLogo();
    }

    return () => {
      isActive = false;
    };
  }, [logoFileId, pendingLogoOperation]);

  const handleAdd = async (
    incomingFiles: File[],
    event: KendoUploadOnAddEvent
  ) => {
    const file = incomingFiles[0];
    if (!file) {
      return;
    }

    // Resize image if needed
    try {
      const processedFile = await fileService.reduceImageFile(
        file,
        MAX_IMAGE_DIMENSION,
        MAX_IMAGE_DIMENSION
      );
      onLogoAdd(processedFile);
    } catch (error) {
      notificationService.showErrorToast(
        `Failed to process ${file.name}. Please try another image.`
      );
      event.affectedFiles.forEach(
        (f) => (f.validationErrors = ['Processing failed'])
      );
    }
  };

  const handleRemove = () => {
    onLogoRemove();
  };
  return (
    <div className="form-group form-group-logo-picker logo-section">
      {files.length > 0 && (
        <div
          className={`logo-preview logo-preview-${
            alignment !== null
              ? FormThemeLogoAlignment[alignment].toLowerCase()
              : 'center'
          }`}
        >
          <div className="logo-image-wrapper">
            {preview && (
              <img src={preview} alt="Logo Preview" className="logo-image" />
            )}
          </div>

          <div className="logo-remove-wrapper">
            <TextButton
              text="Remove"
              type="text-only"
              classNames="remove-logo-btn"
              onClick={handleRemove}
            />
          </div>
        </div>
      )}

      <div className="logo-upload-row">
        <KendoReactFileUpload
          files={files}
          onFilesAdded={handleAdd}
          multiple={false}
          acceptedFileExtensions={DEFAULT_ALLOWED_TYPES}
          disabled={isBusy}
          selectButtonText="Upload Logo"
          dropAreaText="Drag and Drop your files here or click the Upload button"
        />

        <div className="alignment-toggle">
          {alignmentOptions.map((option) => (
            <IconButton
              key={option.value}
              iconClass={`fa-align-${option.label}`}
              title={`Align ${option.label}`}
              classNames={option.value === alignment ? 'active' : ''}
              onClick={() => onAlignmentChange(option.value)}
              disabled={files.length === 0 || isBusy}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
